import { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react'
import TextField from '@mui/material/TextField'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { IIncidentReportFormData } from '../IncidentReportModal'
import PrimaryDropdown from '../../../../global/components/dropdowns/PrimaryDropdown'
import PrimaryCheckbox from '../../../../global/components/checkbox/Checkbox'
import PrimaryButton from '../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../global/components/buttons/secondary-button/SecondaryButton'
import { WHEEL_OFF_REASONS_ARRAY } from '../../../../global/constants/incident-report'
import styles from '../styles.module.css'
import { fetchData } from '../../../../global/utils/fetch'
import {
  getOrderImages,
  uploadIncidentReportImage,
  getIncidentReportImages,
  deleteIncidentReportImage,
} from '../../../api'

interface OrderImage {
  objectId: string
  url: string
  type: string
}

interface IInvestigationTabProps {
  formData: IIncidentReportFormData
  setFormData: Dispatch<SetStateAction<IIncidentReportFormData>>
  orderVehicleObjectId?: string
  incidentReportObjectId?: string
}

function InvestigationTab({
  formData,
  setFormData,
  orderVehicleObjectId,
  incidentReportObjectId,
}: IInvestigationTabProps) {
  const [orderImages, setOrderImages] = useState<OrderImage[]>([])
  const [incidentReportImages, setIncidentReportImages] = useState<
    OrderImage[]
  >([])
  const [isLoadingImages, setIsLoadingImages] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [expandedImage, setExpandedImage] = useState<string | null>(null)
  const [selectedForDeletion, setSelectedForDeletion] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  function formatImageType(type: string): string {
    if (!type) return 'Unknown'
    return type
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  function toggleDeletionSelection(imageObjectId: string) {
    setSelectedForDeletion((prev) =>
      prev.includes(imageObjectId)
        ? prev.filter((id) => id !== imageObjectId)
        : [...prev, imageObjectId],
    )
  }

  async function handleDeleteSelected() {
    if (selectedForDeletion.length === 0) return

    setIsDeleting(true)
    try {
      await Promise.all(
        selectedForDeletion.map((imageObjectId) =>
          fetchData(deleteIncidentReportImage(imageObjectId)),
        ),
      )

      // Refresh incident report images
      if (incidentReportObjectId) {
        const updatedImages = await fetchData<OrderImage[]>(
          getIncidentReportImages(incidentReportObjectId),
        )
        setIncidentReportImages(updatedImages || [])
      }
      setSelectedForDeletion([])
    } catch (error) {
      console.error('Error deleting images:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    async function fetchImages() {
      setIsLoadingImages(true)
      try {
        const promises: Promise<any>[] = []

        if (orderVehicleObjectId) {
          promises.push(
            fetchData<OrderImage[]>(getOrderImages(orderVehicleObjectId)),
          )
        } else {
          promises.push(Promise.resolve([]))
        }

        if (incidentReportObjectId) {
          promises.push(
            fetchData<OrderImage[]>(
              getIncidentReportImages(incidentReportObjectId),
            ),
          )
        } else {
          promises.push(Promise.resolve([]))
        }

        const [orderImgs, incidentImgs] = await Promise.all(promises)
        setOrderImages(orderImgs || [])
        setIncidentReportImages(incidentImgs || [])
      } catch (error) {
        console.error('Error fetching images:', error)
      } finally {
        setIsLoadingImages(false)
      }
    }
    fetchImages()
  }, [orderVehicleObjectId, incidentReportObjectId])
  function handleChange(field: keyof IIncidentReportFormData, value: any) {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  function handleAccidentDataChange(field: string, value: any) {
    setFormData((prev) => ({
      ...prev,
      accidentData: {
        ...prev.accidentData,
        [field]: value,
      },
    }))
  }

  function handleImageSelection(imageObjectId: string, isSelected: boolean) {
    setFormData((prev) => ({
      ...prev,
      selectedImageObjectIds: isSelected
        ? [...prev.selectedImageObjectIds, imageObjectId]
        : prev.selectedImageObjectIds.filter((id) => id !== imageObjectId),
    }))
  }

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file || !incidentReportObjectId) return

    setIsUploading(true)
    try {
      const formDataToUpload = new FormData()
      formDataToUpload.append('file', file)

      await fetchData(
        uploadIncidentReportImage(incidentReportObjectId, formDataToUpload),
      )

      // Refresh incident report images
      const updatedImages = await fetchData<OrderImage[]>(
        getIncidentReportImages(incidentReportObjectId),
      )
      setIncidentReportImages(updatedImages || [])
    } catch (error) {
      console.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const wheelOffReasonItems = WHEEL_OFF_REASONS_ARRAY.map((reason) => ({
    key: reason,
    value: reason.replace(/_/g, ' '),
  }))

  return (
    <div className={styles.formContainer}>
      <TextField
        label='Investigation Summary'
        value={formData.investigationSummary}
        onChange={(e) => handleChange('investigationSummary', e.target.value)}
        multiline
        rows={4}
        fullWidth
      />

      <TextField
        label='Employee Statement'
        value={formData.employeeStatement}
        onChange={(e) => handleChange('employeeStatement', e.target.value)}
        multiline
        rows={4}
        fullWidth
      />

      {formData.wheelOff && (
        <>
          <div className={styles.sectionTitle}>Wheel Off Details</div>
          <div style={{ maxWidth: 300 }}>
            <PrimaryDropdown
              label='Wheel Off Reason'
              defaultValue={formData.wheelOffReason}
              onChange={(value) => handleChange('wheelOffReason', value)}
              items={wheelOffReasonItems}
            />
          </div>
        </>
      )}

      <div className={styles.sectionTitle}>Accident Information</div>
      <PrimaryCheckbox
        label='Was this a DOT Recordable Accident?'
        checked={formData.isAccident}
        onChange={(_, checked) => handleChange('isAccident', checked)}
      />

      {formData.isAccident && (
        <>
          <div className={styles.formRow}>
            <TextField
              label='Driver Name'
              value={formData.accidentData.driverName || ''}
              onChange={(e) =>
                handleAccidentDataChange('driverName', e.target.value)
              }
              sx={{ flex: 1 }}
            />
            <TextField
              label='City/Town, State'
              value={formData.accidentData.cityState || ''}
              onChange={(e) =>
                handleAccidentDataChange('cityState', e.target.value)
              }
              sx={{ flex: 1 }}
            />
          </div>

          <div className={styles.formRow}>
            <TextField
              label='# of Injuries'
              type='number'
              value={formData.accidentData.numberOfInjuries ?? ''}
              onChange={(e) =>
                handleAccidentDataChange(
                  'numberOfInjuries',
                  e.target.value ? Number(e.target.value) : 0,
                )
              }
              sx={{ flex: 1 }}
            />
            <TextField
              label='# of Fatalities'
              type='number'
              value={formData.accidentData.numberOfFatalities ?? ''}
              onChange={(e) =>
                handleAccidentDataChange(
                  'numberOfFatalities',
                  e.target.value ? Number(e.target.value) : 0,
                )
              }
              sx={{ flex: 1 }}
            />
          </div>

          <div className={styles.formRow}>
            <PrimaryCheckbox
              label='Vehicle Towed'
              checked={formData.accidentData.isVehicleTowed || false}
              onChange={(_, checked) =>
                handleAccidentDataChange('isVehicleTowed', checked)
              }
            />
            <PrimaryCheckbox
              label='Hazmat Released'
              checked={formData.accidentData.isHazmatRelease || false}
              onChange={(_, checked) =>
                handleAccidentDataChange('isHazmatRelease', checked)
              }
            />
            <PrimaryCheckbox
              label='Driver Citation'
              checked={formData.accidentData.isDriverCitation || false}
              onChange={(_, checked) =>
                handleAccidentDataChange('isDriverCitation', checked)
              }
            />
          </div>
        </>
      )}

      {/* Images Section */}
      <div className={styles.sectionTitle}>Images</div>

      {isLoadingImages ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {/* Order Images (Installation Photos) */}
          {orderImages.length > 0 && (
            <>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Installation Photos (select to include)
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                {orderImages.map((image) => (
                  <div
                    key={image.objectId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: '1px solid var(--border-default)',
                      borderRadius: '4px',
                      padding: '8px',
                      width: '130px',
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.type}
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: '4px',
                      }}
                      onClick={() => setExpandedImage(image.url)}
                    />
                    <div
                      style={{
                        fontSize: '11px',
                        marginTop: '4px',
                        textAlign: 'center',
                      }}
                    >
                      {formatImageType(image.type)}
                    </div>
                    <PrimaryCheckbox
                      label='Include'
                      checked={formData.selectedImageObjectIds.includes(
                        image.objectId,
                      )}
                      onChange={(_, checked) =>
                        handleImageSelection(image.objectId, checked)
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Incident Report Uploaded Images */}
          {incidentReportImages.length > 0 && (
            <>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Uploaded Images
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                {incidentReportImages.map((image) => (
                  <div
                    key={image.objectId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      border: selectedForDeletion.includes(image.objectId)
                        ? '2px solid var(--brand-primary)'
                        : '1px solid var(--border-default)',
                      borderRadius: '4px',
                      padding: '8px',
                      width: '130px',
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.type || 'Uploaded'}
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: '4px',
                      }}
                      onClick={() => setExpandedImage(image.url)}
                    />
                    <div
                      style={{
                        fontSize: '11px',
                        marginTop: '4px',
                        textAlign: 'center',
                      }}
                    >
                      {formatImageType(image.type) || 'Uploaded'}
                    </div>
                    <PrimaryCheckbox
                      label='Select'
                      checked={selectedForDeletion.includes(image.objectId)}
                      onChange={() => toggleDeletionSelection(image.objectId)}
                    />
                  </div>
                ))}
              </div>
              {selectedForDeletion.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <SecondaryButton
                    buttonName={isDeleting ? 'Removing...' : `Remove Selected`}
                    onClick={handleDeleteSelected}
                    disabled={isDeleting}
                  />
                </div>
              )}
            </>
          )}

          {/* Upload Button */}
          <div style={{ marginTop: '16px' }}>
            <input
              type='file'
              accept='image/*'
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={!incidentReportObjectId || isUploading}
            />
            <SecondaryButton
              buttonName={isUploading ? 'Uploading...' : 'Upload Image'}
              onClick={() => fileInputRef.current?.click()}
              disabled={!incidentReportObjectId || isUploading}
            />
            {!incidentReportObjectId && (
              <div
                style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}
              >
                Save the incident report first to upload images
              </div>
            )}
          </div>
        </>
      )}

      {/* Expanded Image Modal */}
      <Modal
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        >
          {expandedImage && (
            <img
              src={expandedImage}
              alt='Expanded'
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
              }}
            />
          )}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <SecondaryButton
              buttonName='Close'
              onClick={() => setExpandedImage(null)}
            />
          </div>
        </Box>
      </Modal>
    </div>
  )
}

export default InvestigationTab
